import styled from '@emotion/styled'
import { withClass } from 'common/emotion'

import { breakWords } from 'component-library/__styling/style-mixins'
import { colors, metrics } from 'component-library/__styling/settings'
import {
    calcRem,
    breakpointMax,
    breakpointBetween,
    breakpoint,
} from 'component-library/__styling/style-functions'

import {
    SponsoredLabel,
    Props as SponsoredLabelProps,
} from 'component-library/advertising/SponsoredLabel/SponsoredLabel'
import { CardHeadline } from 'component-library/cards/CardText/CardHeadline'
import { CardMedia } from 'component-library/cards/CardMedia/CardMedia'
import { StyledLink as CardTextInteractions } from 'component-library/cards/CardText/CardTextInteractions.styled'
import { styledHeadlineTextClassName } from 'component-library/cards/CardText/CardText.styled'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { themedValue } from 'component-library/__styling/themed-value'
import { CardTeaser } from '../CardText/CardTeaser'
import { CardHeadlineSkeleton } from '../CardText/CardHeadlineSkeleton'
import { Timestamp } from 'component-library/content/CardTimestamp/CardTimestamp'

export const styledLandscapeClassName = 'LandscapeCard'

export interface StyledLandscapeProps {
    showSponsoredStyles?: boolean
    hasBackground?: boolean
    edgeToEdge?: boolean
    hasPadding?: boolean
}

export const getLandscapeWrapperTag = (tag: 'div' | 'li') => {
    return withClass(styledLandscapeClassName)(
        styled(tag || 'div')<StyledLandscapeProps>(
            {
                ...breakWords,
                position: 'relative',
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                flexGrow: 1,
                alignItems: 'flex-start',
            },
            ({ showSponsoredStyles, hasBackground, hasPadding, edgeToEdge, theme }) => ({
                background: hasBackground ? colors.white : undefined,
                padding: themedValue(theme, {
                    thewest: undefined,
                    fallback: hasPadding ? calcRem(theme.margins.sm) : undefined,
                }),

                // prettier-ignore
                borderBottom: themedValue(theme, {
                    thewest: undefined,
                    perthnow: undefined,
                    sevennews: showSponsoredStyles ? `2px solid ${colors.black}` : `1px solid ${colors.perthnow.greyMarble}`,
                }),

                // Remove horizontal padding only
                ...conditionalStyles(edgeToEdge, {
                    padding: edgeToEdge
                        ? `${calcRem(theme.margins.sm)} 0`
                        : calcRem(theme.margins.sm),
                }),
            })
        )
    )
}

// Adds in the width and alignment styles for the wrapping link - hovers, focus, link styles come from CardTextInteractions component
export const StyledLandscapeLink = styled(CardTextInteractions)({
    display: 'flex',
    flex: '1 3 auto',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    padding: 0,
})
StyledLandscapeLink.displayName = 'LandscapeLink'

export interface StyledHeaderProps {
    hideKicker?: boolean
}

export const StyledHeader = styled('div')<StyledHeaderProps>(({ theme, hideKicker }) => ({
    fontSize: themedValue(theme, {
        thewest: calcRem(18),
        fallback: calcRem(16),
    }),
    lineHeight: 1.375,
    flexDirection: 'column',
    justifyContent: 'space-between',
    display: 'flex',
    flexWrap: 'wrap',
    alignSelf: 'stretch',
    flex: `1 2 ${calcRem(120)}`,
    padding: themedValue(theme, {
        thewest: hideKicker ? calcRem(8, 8, 4, 0) : calcRem(0, 8, 4, 0),
        perthnow: calcRem(8, 8, 4, 0),
        sevennews: calcRem(8, 8, 4, 0),
    }),

    [breakpoint('sm')]: {
        fontSize: calcRem(16),
    },
}))
StyledHeader.displayName = 'LandscapeHeaderWrapper'

// CardHeadline component alignment and fontsize overrides
export const StyledHeadline = styled(CardHeadline)(({ theme }) => ({
    margin: 0,
    width: '100%',
    flex: 'none', // overide default headline flexbox from CardText component
    fontSize: themedValue(theme, {
        thewest: calcRem(16),
        fallback: undefined,
    }),

    [breakpoint('sm')]: {
        fontSize: themedValue(theme, {
            thewest: calcRem(14),
            fallback: undefined,
        }),
    },

    [`& .${styledHeadlineTextClassName}`]: {
        transform: `translateY(-${calcRem(theme.cards.textOffset)})`,
    },
}))
StyledHeadline.displayName = 'LandscapeHeadline'

export const StyledCardHeadlineSkeleton = styled(CardHeadlineSkeleton)({
    margin: 0,
    width: '100%',
    flex: 'none', // overide default headline flexbox from CardText component

    [`& .${styledHeadlineTextClassName}`]: {
        transform: `translateY(-${calcRem(metrics.perthnow.cards.textOffset)})`,
    },
})

export interface StyledMediaProps {
    imagePosition: 'left' | 'right'
    smallImage?: boolean
}

const xsImageWidth = 95
const mobileImageWidth = 110

// CardMedia component width & margin overrides
export const StyledMedia = styled(CardMedia)<StyledMediaProps>(
    ({ imagePosition, smallImage, theme }) => ({
        flex: `1 3 ${calcRem(106)}`,
        maxWidth: smallImage ? calcRem(90) : calcRem(140),
        margin:
            imagePosition === 'right'
                ? `0 0 0 ${calcRem(theme.margins.sm)}`
                : `0 ${calcRem(theme.margins.sm)} 0 0`,

        ...conditionalStyles(theme.kind === 'thewest', {
            [breakpointMax('sm')]: {
                maxWidth: calcRem(mobileImageWidth),
            },

            [breakpointBetween('sm', 'md')]: {
                maxWidth: calcRem(mobileImageWidth),
            },

            // Custom breakpoint for when landscape cards are in columns on the west
            '@media screen and (min-width: 1020px) and (max-width: 1070px)': {
                maxWidth: calcRem(xsImageWidth),
            },
        }),
    })
)
StyledMedia.displayName = 'LandscapeMedia'

// CardTeaser component font size overrides
export const StyledTeaser = styled(CardTeaser)({
    fontSize: calcRem(14),
})

// SponsoredLabel component overrides for margins and wrapping
export const StyledSponsoredLabel = styled(SponsoredLabel)<SponsoredLabelProps>(({ theme }) => ({
    marginTop: theme.margins.sm,
    whiteSpace: themedValue(theme, {
        sevennews: undefined,
        fallback: 'unset' as 'unset',
    }),
    ...conditionalStyles(theme.kind === 'sevennews', {
        flex: 'auto',
        textAlign: 'right',
        alignSelf: 'flex-end',
        whiteSpace: 'normal',
    }),
}))
StyledSponsoredLabel.displayName = 'LandscapeSponsoredLabel'

export const StyledTimestamp = styled(Timestamp)(({ theme }) => ({
    color: themedValue(theme, {
        thewest: colors.thewest.greyElephant,
        fallback: colors.black,
    }),
}))

export const StyledInlineWrapper = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
})
